// src/modules/customers/components/CustomerEditControl.jsx
// Approval-gated customer editing — the customer twin of the pledge case
// ReconcileControl. Non-admins request the fields they need to change; once
// approved those fields unlock for one save. Admins edit directly.
import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Button, Checkbox, Form, Input, Modal, Popconfirm, Space, Table, Tag, Typography, message,
} from 'antd';
import { EditOutlined, UnlockOutlined, ClockCircleOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { getCustomerEditState, requestCustomerEdit, cancelCustomerEdit } from '../api';

const { Text } = Typography;

const fmtDateTime = (d) => (d ? dayjs(d).format('DD MMM YYYY, hh:mm A') : '—');

const STATUS_TAG = {
  CONSUMED: { color: 'green', label: 'Edited' },
  ADMIN_EDIT: { color: 'blue', label: 'Admin edit' },
  REJECTED: { color: 'red', label: 'Rejected' },
  CANCELLED: { color: 'default', label: 'Cancelled' },
  EXPIRED: { color: 'default', label: 'Expired unused' },
  PENDING: { color: 'orange', label: 'Pending' },
  UNLOCKED: { color: 'green', label: 'Approved' },
};

/** Loads the edit state for a customer; `reload` after any change. */
export function useCustomerEdit(customerId) {
  const [state, setState] = useState(null);
  const reload = useCallback(async () => {
    if (!customerId) return;
    try {
      const res = await getCustomerEditState(customerId);
      setState(res.data);
    } catch {
      setState(null);
    }
  }, [customerId]);
  useEffect(() => { reload(); }, [reload]);
  return { state, reload };
}

const labelMap = (state) => Object.fromEntries((state?.fields || []).map((f) => [f.key, f.label]));
const labelsOf = (state, keys = []) => {
  const m = labelMap(state);
  return keys.map((k) => m[k] || k).join(', ');
};

export function RequestEditModal({ open, customerId, state, onClose, onSubmitted, initialFields }) {
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);
  const [messageApi, contextHolder] = message.useMessage();

  const submit = async () => {
    let values;
    try { values = await form.validateFields(); } catch { return; }
    setSubmitting(true);
    try {
      const res = await requestCustomerEdit(customerId, values);
      messageApi.success(res.message || 'Edit request submitted for approval');
      if (res.workflowWarning) messageApi.warning(res.workflowWarning, 8);
      form.resetFields();
      onSubmitted?.();
      onClose?.();
    } catch (err) {
      messageApi.error(err?.response?.data?.message || 'Failed to submit edit request');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      title="Request customer edit"
      open={open}
      onOk={submit}
      okText="Submit for approval"
      confirmLoading={submitting}
      onCancel={onClose}
      destroyOnClose
    >
      {contextHolder}
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message="Customer details are locked"
        description={`Pick the fields you need to change. Once approved, only those fields can be edited, for one save${state?.unlockTtlDays ? ` within ${state.unlockTtlDays} days` : ''}.`}
      />
      <Form form={form} layout="vertical" initialValues={{ fields: initialFields }}>
        <Form.Item
          name="fields"
          label="Fields to change"
          rules={[{ required: true, message: 'Select at least one field' }]}
        >
          <Checkbox.Group style={{ width: '100%' }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 8 }}>
              {(state?.fields || []).map((f) => <Checkbox key={f.key} value={f.key}>{f.label}</Checkbox>)}
            </div>
          </Checkbox.Group>
        </Form.Item>
        <Form.Item
          name="reason"
          label="Why does it need to change?"
          rules={[{ required: true, min: 10, message: 'Please explain (at least 10 characters)' }]}
        >
          <Input.TextArea rows={3} placeholder="e.g. Customer changed their mobile number" />
        </Form.Item>
      </Form>
    </Modal>
  );
}

/**
 * Header buttons. Admins get a plain Edit button; everyone else gets
 * Request Edit / Edit approved fields / Cancel depending on the request state.
 */
export function CustomerEditActions({ customerId, isAdmin, canEdit, state, reload, onEdit }) {
  const [modalOpen, setModalOpen] = useState(false);
  const [messageApi, contextHolder] = message.useMessage();
  if (!canEdit) return null;

  if (isAdmin) {
    return <Button type="primary" icon={<EditOutlined />} onClick={onEdit}>Edit Customer</Button>;
  }

  const r = state?.editRequest || { status: 'NONE' };
  const cancel = async () => {
    try {
      await cancelCustomerEdit(customerId);
      messageApi.success('Edit request cancelled');
      reload();
    } catch (err) {
      messageApi.error(err?.response?.data?.message || 'Failed to cancel');
    }
  };

  return (
    <>
      {contextHolder}
      {r.status === 'UNLOCKED' && (r.fields || []).every((f) => f === 'bankAccounts') && (
        <Space>
          <Tag icon={<UnlockOutlined />} color="green">Bank details unlocked — edit below</Tag>
          <Popconfirm title="Give back this approval without editing?" onConfirm={cancel}>
            <Button size="small">Cancel</Button>
          </Popconfirm>
        </Space>
      )}
      {r.status === 'UNLOCKED' && !(r.fields || []).every((f) => f === 'bankAccounts') && (
        <Space>
          <Button type="primary" icon={<UnlockOutlined />} onClick={onEdit}>Edit approved fields</Button>
          <Popconfirm title="Give back this approval without editing?" onConfirm={cancel}>
            <Button>Cancel</Button>
          </Popconfirm>
        </Space>
      )}
      {r.status === 'PENDING' && (
        <Space>
          <Tag icon={<ClockCircleOutlined />} color="orange">Edit request pending</Tag>
          <Popconfirm title="Withdraw this edit request?" onConfirm={cancel}>
            <Button size="small">Cancel request</Button>
          </Popconfirm>
        </Space>
      )}
      {!['UNLOCKED', 'PENDING'].includes(r.status) && (
        <Button type="primary" icon={<EditOutlined />} onClick={() => setModalOpen(true)} disabled={!state}>
          Request Edit
        </Button>
      )}
      <RequestEditModal
        open={modalOpen}
        customerId={customerId}
        state={state}
        onClose={() => setModalOpen(false)}
        onSubmitted={reload}
      />
    </>
  );
}

/** Status banner under the header: pending / approved / just rejected or expired. */
export function CustomerEditBanner({ state }) {
  if (!state) return null;
  const r = state.editRequest || { status: 'NONE' };

  if (r.status === 'PENDING') {
    return (
      <Alert
        type="warning"
        showIcon
        style={{ marginBottom: 16 }}
        message={`Edit request awaiting approval: ${labelsOf(state, r.fields)}`}
        description={`Reason: ${r.reason || '—'} · Requested ${fmtDateTime(r.requestedAt)}${r.requestedBy?.name ? ` by ${r.requestedBy.name}` : ''}`}
      />
    );
  }
  if (r.status === 'UNLOCKED') {
    const until = r.unlockedAt ? dayjs(r.unlockedAt).add(state.unlockTtlDays || 7, 'day') : null;
    return (
      <Alert
        type="success"
        showIcon
        style={{ marginBottom: 16 }}
        message={`Approved — you can now edit: ${labelsOf(state, r.fields)}`}
        description={`One save only${until ? `, before ${until.format('DD MMM YYYY')}` : ''}. The customer locks again after it.`}
      />
    );
  }
  const last = state.history?.[0];
  if (last && ['REJECTED', 'EXPIRED'].includes(last.status)) {
    return (
      <Alert
        type={last.status === 'REJECTED' ? 'error' : 'info'}
        showIcon
        closable
        style={{ marginBottom: 16 }}
        message={last.status === 'REJECTED'
          ? `Last edit request was rejected (${labelsOf(state, last.fields)})`
          : `Last approved edit expired unused (${labelsOf(state, last.fields)})`}
        description={last.status === 'REJECTED' ? `Reason: ${last.rejectionReason || '—'}` : undefined}
      />
    );
  }
  return null;
}

const showValue = (field, v) => {
  if (v === null || v === undefined || v === '') return <Text type="secondary">empty</Text>;
  if (field === 'photo') return <a href={v} target="_blank" rel="noreferrer">photo</a>;
  if (field === 'dateOfBirth') return dayjs(v).format('DD/MM/YYYY');
  if (field === 'bankAccounts') {
    if (!Array.isArray(v) || !v.length) return <Text type="secondary">none</Text>;
    return v.map((a) => `${a.ifscCode} ••••${String(a.accountNumber || '').slice(-4)}${a.isPrimary ? ' (primary)' : ''}`).join('; ');
  }
  return String(v);
};

/** Every past request / edit with old → new values. */
export function CustomerEditHistory({ state }) {
  const history = state?.history || [];
  if (!history.length) return <Text type="secondary">No edits yet.</Text>;
  const labels = labelMap(state);

  const columns = [
    {
      title: 'Outcome',
      dataIndex: 'status',
      width: 130,
      render: (s) => <Tag color={STATUS_TAG[s]?.color}>{STATUS_TAG[s]?.label || s}</Tag>,
    },
    {
      title: 'Changes',
      render: (_, h) => {
        const changed = h.changedFields?.length ? h.changedFields : null;
        if (!changed) return <Text type="secondary">{(h.fields || []).map((f) => labels[f] || f).join(', ')}</Text>;
        return (
          <Space direction="vertical" size={2}>
            {changed.map((f) => (
              <span key={f}>
                <Text strong>{labels[f] || f}:</Text>{' '}
                {showValue(f, h.snapshotBefore?.[f])} → {showValue(f, h.snapshotAfter?.[f])}
              </span>
            ))}
          </Space>
        );
      },
    },
    {
      title: 'Reason',
      render: (_, h) => h.reason || (h.status === 'ADMIN_EDIT' ? <Text type="secondary">Direct admin edit</Text> : '—'),
    },
    {
      title: 'By',
      width: 150,
      render: (_, h) => h.consumedBy?.name || h.requestedBy?.name || '—',
    },
    {
      title: 'When',
      width: 170,
      render: (_, h) => fmtDateTime(h.consumedAt || h.rejectedAt || h.cancelledAt || h.expiredAt || h.requestedAt),
    },
  ];

  return (
    <Table
      rowKey={(h, i) => `${h.requestedAt || ''}-${i}`}
      size="small"
      columns={columns}
      dataSource={history}
      pagination={history.length > 10 ? { pageSize: 10 } : false}
      scroll={{ x: 700 }}
    />
  );
}
